import type { Dispatch, RefObject, SetStateAction } from 'react'

import { useCallback, useLayoutEffect } from 'react'
import { debounce } from 'utils/helpers'

type PropTypes = {
  tabsRef: RefObject<HTMLDivElement | null>
  activeTab: number
  itemCount?: number
  leftButtonVisible: boolean
  setLeftButtonVisible: Dispatch<SetStateAction<boolean>>
  rightButtonVisible: boolean
  setRightButtonVisible: Dispatch<SetStateAction<boolean>>
  setIsScrolling: Dispatch<SetStateAction<boolean>>
  setScrollLeft: Dispatch<SetStateAction<number | undefined>>
  setCount: Dispatch<SetStateAction<number>>
  clientWidth: number
  groupCell?: boolean
}

export const useTabsScroll = ({
  tabsRef,
  activeTab,
  leftButtonVisible,
  setLeftButtonVisible,
  rightButtonVisible,
  setRightButtonVisible,
  setIsScrolling,
  setScrollLeft,
  setCount,
  itemCount,
  clientWidth,
  groupCell,
}: PropTypes) => {
  const DELAY_SET_SCROLL_POSITION = 150

  const scrollToItem = (nextItemIndex: number) => {
    if (tabsRef.current) {
      tabsRef.current.children[nextItemIndex]?.scrollIntoView({
        block: 'nearest',
        inline: 'start',
        behavior: 'smooth',
      })
    }
  }

  const scrollItemGroup = (next?: boolean) => {
    if (tabsRef.current) {
      const { clientWidth, scrollLeft } = tabsRef.current
      const widthNext = clientWidth + scrollLeft
      const widthPrev = scrollLeft - clientWidth
      const array = Array.from(tabsRef.current.children) as HTMLDivElement[]
      if (next) {
        return array.findIndex(
          ({ offsetLeft, offsetWidth }) => widthNext <= offsetLeft + offsetWidth
        )
      } else {
        return array.findIndex(
          ({ offsetLeft, offsetWidth }) => widthPrev <= offsetLeft - offsetWidth
        )
      }
    }
  }

  const next = () => {
    const currentTab = groupCell ? scrollItemGroup(true) : activeTab + 1
    scrollToItem(currentTab!)

    if (!leftButtonVisible) setLeftButtonVisible(true)
  }

  const prev = () => {
    const currentTab = groupCell ? scrollItemGroup(false) : activeTab - 1
    scrollToItem(currentTab!)

    if (!rightButtonVisible) {
      setRightButtonVisible(true)
    }
    if (activeTab === 0) {
      setLeftButtonVisible(false)
    }
  }

  const handleScroll = useCallback(
    debounce(() => {
      if (tabsRef.current) {
        setIsScrolling(true)
        setScrollLeft(tabsRef.current.scrollLeft)
        setIsScrolling(false)
      }
    }, DELAY_SET_SCROLL_POSITION),
    []
  )

  useLayoutEffect(() => {
    setCount(itemCount || 0)
    handleScroll()
    setScrollLeft((prevScrollLeft) => prevScrollLeft! + 1) // required to update the scroll position and the appearance of controls if necessary
  }, [itemCount])

  useLayoutEffect(() => {
    const element = tabsRef.current

    if (element) {
      const scrollLeft = element.scrollLeft
      const scrollWidth = element.scrollWidth

      const onWheel = (e: globalThis.WheelEvent) => {
        const isStartLimitation = scrollLeft === 0 && e.deltaY < 0
        const isEndLimitation =
          scrollWidth - clientWidth - Math.round(scrollLeft) <= 0 &&
          e.deltaY > 0

        if (!e.deltaY) return

        if (!isStartLimitation && !isEndLimitation) {
          e.preventDefault()
        }
        element.scrollTo({
          left: element.scrollLeft + e.deltaY,
          behavior: 'smooth',
        })
        setRightButtonVisible(
          element.scrollWidth - element.clientWidth !==
            Math.round(element.scrollLeft)
        )
      }

      element.addEventListener('wheel', onWheel)
      return () => {
        element.removeEventListener('wheel', onWheel)
      }
    }
  }, [clientWidth, tabsRef.current?.scrollWidth, tabsRef.current?.scrollLeft])

  return { handleScroll, next, prev }
}
